import React, { useState, useEffect } from 'react';
import Input from 'components/ui/Input';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

/**
 * Validates Italian Partita IVA using official Agenzia delle Entrate algorithm
 * Reference: Modulo 10 check digit validation for Italian VAT numbers
 * @param {string} piva - The Partita IVA to validate (11 digits)
 * @returns {boolean} - True if valid, false otherwise
 */
const validatePartitaIVA = (piva) => {
  // Must be exactly 11 digits
  if (!piva || piva?.length !== 11 || !/^\d{11}$/?.test(piva)) {
    return false;
  }

  // Reject trivially invalid numbers where all digits are identical (e.g. "00000000000")
  if (/^(\d)\1{10}$/?.test(piva)) {
    return false;
  }

  // Extract the check digit (last digit)
  const checkDigit = parseInt(piva?.[10], 10);
  
  // Calculate expected check digit using official algorithm
  let sum = 0;
  
  for (let i = 0; i < 10; i++) {
    let digit = parseInt(piva?.[i], 10);
    
    // Even positions (0-indexed: 1, 3, 5, 7, 9) are doubled
    if (i % 2 === 1) {
      digit *= 2;
      // If result is > 9, subtract 9 (equivalent to summing digits)
      if (digit > 9) {
        digit -= 9;
      }
    }
    
    sum += digit;
  }
  
  // Calculate expected check digit
  const expectedCheckDigit = (10 - (sum % 10)) % 10;
  
  return checkDigit === expectedCheckDigit;
};

const VatSection = ({ vatNumber, onChange }) => {
  const [validationStatus, setValidationStatus] = useState(null); // null | 'valid' | 'invalid' | 'blocked' | 'duplicate'
  const [displayValue, setDisplayValue] = useState(vatNumber || '');

  // Validate whenever vatNumber changes
  useEffect(() => {
    if (!vatNumber || vatNumber?.length === 0) {
      setValidationStatus(null);
      return;
    }

    // Only validate if exactly 11 digits
    if (vatNumber?.length === 11) {
      const isValid = validatePartitaIVA(vatNumber);
      if (!isValid) {
        setValidationStatus('invalid');
        return;
      }

      // Checksum passed — run async checks sequentially
      const runAsyncChecks = async () => {
        // Step 1: Check blocklist
        const { data: blockedRow } = await supabase?.from('blocked_partita_iva')?.select('vat_number')?.eq('vat_number', vatNumber)?.maybeSingle();

        if (blockedRow) {
          setValidationStatus('blocked');
          return;
        }

        // Step 2: Check for duplicate registration
        const { data: existingUser } = await supabase?.from('users')?.select('id')?.eq('partita_iva', vatNumber)?.eq('role', 'professionista')?.maybeSingle();

        if (existingUser) {
          setValidationStatus('duplicate');
          return;
        }

        setValidationStatus('valid');
      };

      runAsyncChecks();
    } else {
      setValidationStatus(null);
    }
  }, [vatNumber]);

  // Sync display value with prop
  useEffect(() => {
    setDisplayValue(vatNumber || '');
  }, [vatNumber]);

  const handleInputChange = (e) => {
    const value = e?.target?.value || '';
    
    // Accept only numeric input
    const numericValue = value?.replace(/\D/g, '');
    
    // Limit to 11 digits
    const limitedValue = numericValue?.slice(0, 11);
    
    setDisplayValue(limitedValue);
    onChange?.('vatNumber', limitedValue);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        Partita IVA
      </h2>
      <p className="text-sm text-gray-600 mb-4">
        Inserisci il tuo numero di Partita IVA. Questo dato è necessario per garantire la trasparenza e la professionalità sulla piattaforma.
      </p>
      <div className="space-y-4">
        <Input
          type="text"
          placeholder="12345678901"
          value={displayValue}
          onChange={handleInputChange}
          label="Numero Partita IVA"
          maxLength={11}
        />
        
        {/* Validation feedback */}
        {(validationStatus === 'invalid' || validationStatus === 'blocked') && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 px-4 py-3 rounded-md">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>Questa Partita IVA non risulta valida. Verifica di averla inserita correttamente.</span>
          </div>
        )}

        {validationStatus === 'duplicate' && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 px-4 py-3 rounded-md">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>Questa Partita IVA risulta già registrata sulla piattaforma.</span>
          </div>
        )}
        
        {validationStatus === 'valid' && (
          <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-4 py-3 rounded-md">
            <CheckCircle className="h-4 w-4 flex-shrink-0" />
            <span>Partita IVA formalmente valida</span>
          </div>
        )}
        
        {validationStatus === null && displayValue?.length > 0 && displayValue?.length < 11 && (
          <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 px-4 py-3 rounded-md">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>Inserisci 11 cifre</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default VatSection;